import React, { useCallback, useRef } from 'react';
import { validate as uuidValidate } from 'uuid';
import { Trans, t } from '@lingui/macro';
import {
    useBreakpoint,
    ConditionalWrapper,
    Tooltip,
    Box,
} from '@landr/maestro';
import { AddSessionToCreator } from 'components/CommonUi/AddSessionToCreator';
import { Pack, PackType, Repack } from 'types/generated/graphql';
import { getStreamEntityType } from 'helpers/getStreamEntityType';
import PageHeader from 'components/PageHeader/PageHeader';
import { FavoriteButton } from 'components/FavoriteButton';
import { DownloadButton } from 'components/CommonUi';
import PlayerButton from 'components/CommonUi/PlayerButton/PlayerButton';
import { InteractionSource } from 'utils/analytics/AnalyticsV2Service.generated';
import { usePartner } from 'hooks/usePartner';
import { PlayerProvider } from 'contexts/PlayerContext/PlayerContext';
import { isPack } from 'types/typeGuards/isPack';
import { getPackPlayButtonClickTrackerV2 } from 'utils/analytics/helpers/trackAssetPlayed';
import { useDynamicBranding } from 'hooks/useDynamicBranding';

const usePackOrRePackActions = ({
    item,
    isTakenDown,
    isLoading,
}: {
    item: Pack | Repack;
    isTakenDown: boolean;
    isLoading: boolean;
}) => {
    const isPack = item?.type === PackType.Pack;
    const isMobile = useBreakpoint('md');
    const buttonRef = useRef<HTMLDivElement>(null);

    const { creatorBrand } = useDynamicBranding();

    const showOpenInCreator =
        item?.creatorSession && uuidValidate(item.creatorSession);

    const hideDemoText = isMobile || showOpenInCreator;

    const firstDownloadLabel = isPack ? t`Get pack` : t`Get collection`;
    const reDownloadLabel = t`Download`;
    const downloadLabel = item?.isDownloaded
        ? reDownloadLabel
        : firstDownloadLabel;
    const isPurchaseButtonDisabled = !item?.isDownloaded && isTakenDown;

    const onPlayBtnClick = useCallback(
        (totalTime: number) => {
            getPackPlayButtonClickTrackerV2({
                interactionSource: InteractionSource.MainAction,
                item: item,
            })(totalTime);
        },
        [item],
    );

    const actions = [];

    if (showOpenInCreator) {
        actions.push(
            <AddSessionToCreator
                key="AddSessionToCreator"
                sessionGuid={item?.creatorSession}
                variant="secondary"
                kind="solid"
                interactionSource="Creator Collection detail"
                isRounded
                isLoading={isLoading}
            >
                <Trans>Open in {creatorBrand}</Trans>
            </AddSessionToCreator>,
        );
    }

    actions.push(
        <ConditionalWrapper
            condition={isPurchaseButtonDisabled}
            wrapper={(children: React.ReactNode): React.ReactNode => (
                <Tooltip label={t`Unavailable`}>
                    <Box>{children}</Box>
                </Tooltip>
            )}
        >
            <DownloadButton
                key="DownloadButton"
                item={item}
                variant={showOpenInCreator ? 'ghost' : 'secondary'}
                kind="solid"
                isRounded
                label={downloadLabel}
                isDisabled={isPurchaseButtonDisabled}
                isLoading={isLoading}
                size={isMobile ? 'lg' : 'md'}
            >
                {hideDemoText ? undefined : <>{downloadLabel}</>}
            </DownloadButton>
        </ConditionalWrapper>,
    );

    actions.push(
        <Box tabIndex={-1} key="PlayerButton">
            <PlayerButton
                id={item.guid}
                variant="ghost"
                callback={onPlayBtnClick}
                isRounded
                keyboardEventConsumerRef={buttonRef}
                isLoading={isLoading}
                size={isMobile ? 'lg' : 'md'}
            >
                {hideDemoText ? undefined : <Trans>Preview</Trans>}
            </PlayerButton>
        </Box>,
    );

    if (!isTakenDown) {
        actions.push(
            <FavoriteButton
                key={`reconciliation-isFavorited-${item.isFavorited}`}
                variant="ghost"
                isRounded
                kind="solid"
                item={item}
                isLoading={isLoading}
                size={isMobile ? 'lg' : 'md'}
            />,
        );
    }

    return actions;
};

const PageHeaderForPackOrRepackLoading = () => {
    return (
        <PageHeader isLoading={true}>
            <PageHeader.Artwork alt={t`Loading`} />
            <PageHeader.Title />
            <PageHeader.Byline />
            <PageHeader.Attributes />
            <PageHeader.Description isLoading={true} />
            <PageHeader.Actions actions={[]} />
        </PageHeader>
    );
};

const PageHeaderForPackOrRepackLoaded = ({
    item,
    isTakenDown,
}: {
    item: Pack | Repack;
    isTakenDown: boolean;
}) => {
    const actions = usePackOrRePackActions({
        item,
        isTakenDown,
        isLoading: false,
    });

    const { name } = useDynamicBranding();

    const partner = usePartner(item);

    const packPartnerName =
        item?.label?.name ?? item?.artist?.name ?? undefined;

    const partnerLink = partner
        ? `/${partner.route}/${partner.slug}`
        : undefined;
    const repackProviderName = t`Collection by ${name}`;

    const partnerName = isPack(item)
        ? packPartnerName?.toUpperCase()
        : repackProviderName;

    const backgroundColor = item?.backgroundColor;
    const foregroundColor = item?.foregroundColor;

    return (
        <PlayerProvider
            playerId={item?.guid}
            streamEntityType={getStreamEntityType(item.type)}
        >
            <PageHeader
                backgroundColor={backgroundColor}
                foregroundColor={foregroundColor}
            >
                <PageHeader.Artwork
                    alt={t`Cover of ${item?.name}`}
                    isTakenDown={isTakenDown}
                    isExclusive={item?.isExclusive}
                    imageUrl={item?.artworkUrl}
                    entity={item}
                />
                <PageHeader.Title>{item?.name}</PageHeader.Title>
                <PageHeader.Byline
                    partnerName={partnerName}
                    partnerLink={partnerLink}
                    sampleCount={item.sampleCount}
                />
                <PageHeader.Attributes attributes={{ genres: item?.genres }} />
                <PageHeader.Description
                    title={item?.name}
                    description={item.description}
                />
                <PageHeader.Actions actions={actions} />
            </PageHeader>
        </PlayerProvider>
    );
};

const PageHeaderForPackOrRepack = ({
    item,
    isTakenDown,
    isLoading,
}: {
    item?: Pack | Repack;
    isTakenDown: boolean;
    isLoading: boolean;
}) => {
    if (isLoading || !item) {
        return <PageHeaderForPackOrRepackLoading />;
    }

    return (
        <PageHeaderForPackOrRepackLoaded
            item={item}
            isTakenDown={isTakenDown}
        />
    );
};

export default PageHeaderForPackOrRepack;
